import { ActionFunction } from 'react-router-dom';
import { ApolloError, type FetchResult } from '@apollo/client';
import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import { getApplicationError } from '@oysterjs/core/graphql/errors';
import { client } from '~/api/client';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import type { DataResult } from '~/api/data/result';
import {
  ArchiveAgencyDesignDocument,
  ArchiveAgencyDesignMutation,
  ArchiveAgencyDesignMutationVariables,
  CreateAgencyDesignDocument,
  CreateAgencyDesignMutation,
  CreateAgencyDesignMutationVariables,
  DuplicateAgencyDesignDocument,
  DuplicateAgencyDesignMutation,
  DuplicateAgencyDesignMutationVariables,
  FileType,
  SetDefaultAgencyDesignDocument,
  SetDefaultAgencyDesignMutation,
  SetDefaultAgencyDesignMutationVariables,
  UpdateAgencyDesignDocument,
  UpdateAgencyDesignMutation,
  UpdateAgencyDesignMutationVariables,
  UploadAgencyBrandLogoDocument,
  UploadAgencyBrandLogoMutation,
  UploadAgencyBrandLogoMutationVariables,
  UploadAgencyDesignPageDocument,
  UploadAgencyDesignPageMutation,
  UploadAgencyDesignPageMutationVariables
} from '~/api/graphql';

export const createAgencyDesign = (variables: CreateAgencyDesignMutationVariables) =>
  mutateData<CreateAgencyDesignMutation, CreateAgencyDesignMutationVariables>(client, {
    mutation: CreateAgencyDesignDocument,
    variables
  });

// An open document learns of a layout change from its refit state, which a save elsewhere in this
// tab would otherwise leave as it was until the tab is next shown.
const REFIT_STATE = ['CaraWorkspaceArtifactRefitState'];

export const updateAgencyDesign = (variables: UpdateAgencyDesignMutationVariables) =>
  mutateData<UpdateAgencyDesignMutation, UpdateAgencyDesignMutationVariables>(client, {
    mutation: UpdateAgencyDesignDocument,
    variables,
    refetchQueries: REFIT_STATE
  });

export const duplicateAgencyDesign = (variables: DuplicateAgencyDesignMutationVariables) =>
  mutateData<DuplicateAgencyDesignMutation, DuplicateAgencyDesignMutationVariables>(client, {
    mutation: DuplicateAgencyDesignDocument,
    variables
  });

export const archiveAgencyDesign = (variables: ArchiveAgencyDesignMutationVariables) =>
  mutateData<ArchiveAgencyDesignMutation, ArchiveAgencyDesignMutationVariables>(client, {
    mutation: ArchiveAgencyDesignDocument,
    variables
  });

export const setDefaultAgencyDesign = (variables: SetDefaultAgencyDesignMutationVariables) =>
  mutateData<SetDefaultAgencyDesignMutation, SetDefaultAgencyDesignMutationVariables>(client, {
    mutation: SetDefaultAgencyDesignDocument,
    variables,
    refetchQueries: REFIT_STATE
  });

/** What a design save says when the session behind it has lapsed. */
export const SESSION_EXPIRED = 'Your session expired. Sign in again in another tab, then save.';

const signedOut = (error: ApolloError | undefined) =>
  getApplicationError(error)?.code === ApplicationErrorCode.Unauthorized;

/**
 * Like submitMutation, but a lapsed session comes back as an error the editor shows. The redirect
 * to sign in would skip the editor's unsaved-changes prompt, as fetcher redirects do, and take
 * the draft and its staged files with it.
 */
const submitDraft = async <T>(result: Promise<DataResult<T>>) => {
  const settled = await result;
  if (signedOut(settled.error)) {
    return {
      applicationError: { code: ApplicationErrorCode.Unauthorized, message: SESSION_EXPIRED }
    };
  }
  return submitMutation(Promise.resolve(settled));
};

export const submitCreateAgencyDesign: ActionFunction = async ({ request }) =>
  submitDraft(createAgencyDesign({ input: await request.json() }));

export const submitUpdateAgencyDesign: ActionFunction = async ({ params, request }) =>
  submitDraft(updateAgencyDesign({ id: params.designId ?? '', input: await request.json() }));

export const submitDuplicateAgencyDesign: ActionFunction = async ({ params }) =>
  submitMutation(duplicateAgencyDesign({ id: params.designId ?? '' }));

export const submitArchiveAgencyDesign: ActionFunction = async ({ params }) =>
  submitMutation(archiveAgencyDesign({ id: params.designId ?? '' }));

export const submitSetDefaultAgencyDesign: ActionFunction = async ({ params }) =>
  submitMutation(setDefaultAgencyDesign({ id: params.designId ?? '' }));

export type UploadedFile = { id: string; name: string; url?: string | null };

/** An upload the server refused, with the reason it gave the user. */
export class UploadRefusedError extends Error {}

/** An upload the server refused because the session behind it has lapsed. */
export class SessionExpiredError extends UploadRefusedError {
  constructor() {
    super(SESSION_EXPIRED);
  }
}

// The client resolves a refused upload rather than throwing, so a missing file is the failure.
// Only an application error's message is relayed; the raw GraphQL message is diagnostics.
const uploaded = <T extends UploadedFile>(
  result: Pick<FetchResult, 'errors'>,
  file: T | null | undefined
): T => {
  if (result.errors?.length) {
    const error = new ApolloError({ graphQLErrors: result.errors });
    if (signedOut(error)) throw new SessionExpiredError();
    const refusal = getApplicationError(error)?.message;
    if (refusal) throw new UploadRefusedError(refusal);
  }
  if (!file?.id) throw new Error('Upload returned no file');
  return file;
};

export const uploadAgencyBrandLogo = async (file: File) => {
  const result = await client.mutate<
    UploadAgencyBrandLogoMutation,
    UploadAgencyBrandLogoMutationVariables
  >({
    mutation: UploadAgencyBrandLogoDocument,
    variables: { file: { file, type: FileType.Other, visible: false, notes: 'Brand logo' } }
  });
  return uploaded(result, result.data?.uploadAgencyBrandLogo);
};

export const uploadAgencyDesignPage = async (file: File) => {
  const result = await client.mutate<
    UploadAgencyDesignPageMutation,
    UploadAgencyDesignPageMutationVariables
  >({
    mutation: UploadAgencyDesignPageDocument,
    variables: { file: { file, type: FileType.Other, visible: false, notes: 'Design page' } }
  });
  return uploaded(result, result.data?.uploadAgencyDesignPage);
};
